import "./style.scss";

import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableColumns } from "components/shared/LastElementTable/type";
import type { IStatusRule } from "components/shared/StatusChip";
import StatusChip from "components/shared/StatusChip";
import StyledButton from "components/shared/StyledButton";
import ThreeDotMenu from "components/shared/ThreeDotMenu";
import { MAINTENANCE_TIME_ZONE } from "helpers/maintenanceTime";
import { formatLocaleDateString } from "helpers/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AppMaintenanceWindow, CreateAppMaintenanceInput } from "store/adminPanelSlice";
import {
    cancelAppMaintenance,
    createAppMaintenance,
    fetchAppMaintenance,
    finishAppMaintenance,
} from "store/adminPanelSlice";
import { applyMaintenanceSignal, fetchCompanyInfo } from "store/settingsStore";
import { useAppDispatch, useAppSelector } from "store/store";

import MaintenanceFormModal from "./MaintenanceFormModal";
import type { MaintenancePreviewContent } from "./MaintenancePreview";
import MaintenancePreview from "./MaintenancePreview";

type MaintenanceStatus = "scheduled" | "active" | "ended" | "cancelled";

const formatWhen = (value: string) =>
    formatLocaleDateString({
        date: value,
        options: { numeric: true, showTime: true, timeZone: MAINTENANCE_TIME_ZONE },
    });

const maintenanceStatus = (window: AppMaintenanceWindow, now: Date): MaintenanceStatus => {
    if (window.cancelledAt) return "cancelled";
    const start = new Date(window.startsAt).getTime();
    const end = new Date(window.endsAt).getTime();
    const current = now.getTime();
    if (current >= start && current < end) return "active";
    if (current < start) return "scheduled";
    return "ended";
};

const MaintenanceSchedule = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const windows = useAppSelector((state) => state.adminPanel.appMaintenances);
    const loadStatus = useAppSelector((state) => state.adminPanel.appMaintenanceStatus);
    const saveStatus = useAppSelector((state) => state.adminPanel.saveAppMaintenanceStatus);
    const [formOpen, setFormOpen] = useState(false);
    const [cancellingId, setCancellingId] = useState<number | null>(null);
    const [finishingId, setFinishingId] = useState<number | null>(null);
    const [screenPreview, setScreenPreview] = useState<MaintenancePreviewContent | null>(null);

    useEffect(() => {
        dispatch(fetchAppMaintenance());
    }, [dispatch]);

    const openForm = () => setFormOpen(true);
    const closePreview = useCallback(() => setScreenPreview(null), []);

    const handleSave = async (schedule: CreateAppMaintenanceInput) => {
        const result = await dispatch(createAppMaintenance(schedule));
        if (!createAppMaintenance.fulfilled.match(result)) return;
        setFormOpen(false);
        const start = new Date(result.payload.startsAt).getTime();
        const end = new Date(result.payload.endsAt).getTime();
        if (Date.now() >= start && Date.now() < end) {
            dispatch(
                applyMaintenanceSignal({
                    startsAt: result.payload.startsAt,
                    endsAt: result.payload.endsAt,
                    title: result.payload.title,
                    description: result.payload.description,
                }),
            );
        }
    };

    const handleCancel = useCallback(
        async (id: number) => {
            setCancellingId(id);
            await dispatch(cancelAppMaintenance(id));
            setCancellingId(null);
        },
        [dispatch],
    );

    const handleFinish = useCallback(
        async (id: number) => {
            setFinishingId(id);
            const result = await dispatch(finishAppMaintenance(id));
            if (finishAppMaintenance.fulfilled.match(result)) {
                dispatch(applyMaintenanceSignal(null));
                dispatch(fetchCompanyInfo());
            }
            setFinishingId(null);
        },
        [dispatch],
    );

    const columns = useMemo((): LastElementTableColumns[] => {
        const statusRules: Record<MaintenanceStatus, IStatusRule> = {
            scheduled: { color: "blue", text: t("AdminPanel.MaintenanceScheduled") },
            active: { color: "green", text: t("AdminPanel.MaintenanceActive") },
            ended: { color: "grey", text: t("AdminPanel.MaintenanceEnded") },
            cancelled: { color: "red", text: t("AdminPanel.MaintenanceCancelled") },
        };
        return [
            {
                id: "title",
                align: "left",
                label: t("AdminPanel.MaintenanceTitle"),
                minWidth: "220px",
                subtitle: "description",
                format: (value: string) => value || "—",
            },
            {
                id: "startsAt",
                align: "left",
                label: `${t("AdminPanel.MaintenanceStartsAt")} (${t("Maintenance.TimeZone")})`,
                format: (value: string) => formatWhen(value),
            },
            {
                id: "endsAt",
                align: "left",
                label: `${t("AdminPanel.MaintenanceEndsAt")} (${t("Maintenance.TimeZone")})`,
                format: (value: string) => formatWhen(value),
            },
            {
                id: "status",
                align: "left",
                label: t("AdminPanel.MaintenanceStatus"),
                format: (_value: string, _id?: number, row?: AppMaintenanceWindow) => {
                    if (!row) return "";
                    return <StatusChip status={maintenanceStatus(row, new Date())} statusRules={statusRules} />;
                },
            },
            {
                id: "actions",
                align: "right",
                label: "",
                stopPropagation: true,
                format: (_value: string, _id?: number, row?: AppMaintenanceWindow) => {
                    if (!row) return "";
                    const status = maintenanceStatus(row, new Date());
                    return (
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                            <ThreeDotMenu
                                options={[
                                    {
                                        id: "preview",
                                        name: t("AdminPanel.MaintenancePreview"),
                                        action: () =>
                                            setScreenPreview({
                                                startsAt: row.startsAt,
                                                endsAt: row.endsAt,
                                                title: row.title,
                                                description: row.description,
                                            }),
                                    },
                                    {
                                        id: "finish",
                                        name: t("AdminPanel.MaintenanceFinish"),
                                        action: () => handleFinish(row.id),
                                        hide: status !== "active",
                                        disabled: finishingId === row.id,
                                    },
                                    {
                                        id: "cancel",
                                        name: t("AdminPanel.MaintenanceCancel"),
                                        action: () => handleCancel(row.id),
                                        hide: status !== "scheduled",
                                        disabled: cancellingId === row.id,
                                    },
                                ]}
                            />
                        </div>
                    );
                },
            },
        ];
    }, [cancellingId, finishingId, handleCancel, handleFinish, t]);

    return (
        <div className="maintenance-schedule">
            {screenPreview && <MaintenancePreview preview={screenPreview} onClose={closePreview} />}
            <MaintenanceFormModal
                open={formOpen}
                saving={saveStatus === "pending"}
                onClose={() => setFormOpen(false)}
                onSave={handleSave}
                onPreview={setScreenPreview}
            />
            <div className="maintenance-schedule-header">
                <div>
                    <p className="maintenance-schedule-intro">{t("AdminPanel.MaintenanceIntro")}</p>
                    <p className="maintenance-schedule-note">{t("AdminPanel.MaintenanceAdminsStay")}</p>
                </div>
                <StyledButton buttonType="primary" allowViewOnly onClick={openForm}>
                    {t("AdminPanel.MaintenanceNew")}
                </StyledButton>
            </div>
            <div className="maintenance-schedule-table">
                <LastElementTable
                    columns={columns}
                    data={windows}
                    dataType="maintenances"
                    max={-1}
                    isLoading={loadStatus === "pending" && windows.length === 0}
                    emptyStateAction={openForm}
                />
            </div>
        </div>
    );
};

export default MaintenanceSchedule;
